import { useWaitingNote } from './useWaitingNote'
import styles from './Waiting.module.css'

interface WaitingProps {
  /** Skips the fade, for the same reason everything else on the stage does. */
  reducedMotion?: boolean
}

/**
 * A sign that something is still happening, when nothing has happened for a
 * while.
 *
 * This is not a status line and it is not a spinner. The design is explicit
 * that the orb is the interface and that progress is the orb rather than a bar
 * beside it, and the orb does carry the wait: it closes and opens on a 2.6
 * second breath that can be held indefinitely without ever reading as a
 * counter. What the orb cannot do is tell the difference between a long turn
 * and a dead one, because it looks the same in both, and a person who cannot
 * tell waiting from broken will reload the page and lose the conversation.
 *
 * So these words exist only past the point where that ambiguity starts. They
 * never appear on an ordinary turn, they say what is happening rather than how
 * far along it is, and the second one is the honest explanation of the worst
 * case: a container that had scaled to zero and is coming back.
 *
 * Nothing here is announced. The line is `aria-hidden` and the same words
 * reach a screen reader through the one `role="status"` region in
 * `LiveRegions`, which is where every other component on this screen sends its
 * announcements too. A second status region beside that one is not twice the
 * accessibility: two polite regions racing means neither is heard reliably.
 */
export function Waiting({ reducedMotion = false }: WaitingProps) {
  const text = useWaitingNote()

  if (text === '') return null

  return (
    <p
      className={styles.note}
      data-still={reducedMotion || undefined}
      data-testid="waiting-note"
      aria-hidden="true"
    >
      {text}
    </p>
  )
}
